/**
 * Step 1: edit the proposed route and decide how incompatible journeys are handled.
 *
 * @remarks
 * This is also the editable all-ready shortcut. The dialog footer chooses direct
 * Save or continuation based on the state hook's readiness.
 *
 * @packageDocumentation
 */
import React from 'react';
import {Alert, Radio, Typography} from 'antd';
import AutoAssignRoutePreview from '../../shared/route-preview';
import type {AutoAssignReview} from '../../state/use-auto-assign-review';

/** Editable route state and availability of the preserve-current-route policy. */
export interface RouteReviewStepProps {
    flow: Pick<
        AutoAssignReview,
        | 'editable'
        | 'extend'
        | 'pending'
        | 'setExtend'
        | 'pendingPassengerCount'
        | 'pendingCargoCount'
        | 'previewProps'
    >;
    hasRoutePolicy: boolean;
}

/** Renders route editing and its pending-assignment explanation. */
export default function RouteReviewStep({flow, hasRoutePolicy}: RouteReviewStepProps) {
    const {editable, extend, pending} = flow;
    return (
        <>
            {editable && hasRoutePolicy && (
                <div className="auto-assign-flow__policy">
                    <Typography.Paragraph strong>Incompatible journeys</Typography.Paragraph>
                    <Radio.Group
                        value={extend}
                        onChange={(event) => flow.setExtend(event.target.value)}
                    >
                        <Radio value={true}>Extend the route to carry selected PAX/CGO</Radio>
                        <Radio value={false}>
                            Keep the current route and review incompatible PAX/CGO
                        </Radio>
                    </Radio.Group>
                </div>
            )}
            {pending.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    message={`${flow.pendingPassengerCount} passengers and ${flow.pendingCargoCount} cargo items need a visit choice.`}
                    description="Some passengers or cargo have more than one possible boarding or drop-off point. Review their assignments in the next step."
                />
            )}

            <div className="flow-section-title">
                <Typography.Title level={4}>Review the proposed route</Typography.Title>
            </div>
            <AutoAssignRoutePreview {...flow.previewProps} />
        </>
    );
}
